import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'
import styled, { useTheme } from 'styled-components/native'

import { type Assignment } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Outcome } from '@lyrahealth-inc/shared-app-logic/src/models/outcome/Outcome'
import { type ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import OutcomeResponseDetails from '@lyrahealth-inc/ui-core/lib/molecules/outcomeResponseDetails/OutcomeResponseDetails'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { AddedAssessments } from './AddedAssessments'
import { AssessmentPendingModal } from './AssessmentPendingModal'
import { CurrentMeasuresTable, type ViewSelfReflectionRequest } from './CurrentMeasuresTable'
import { findOutcomeDateByKey } from './getMeasureRows'
import { parseOutcomeResponseDate } from './outcomeDate'
import { selectAddedAssessmentResults, useAddedAssessmentsData } from './useAddedAssessmentsData'
import {
  getChartAnchorId,
  type OutcomeHistoryChartResponse,
  OutcomeHistoryCharts,
} from '../../../clients/individualClient/OutcomeHistoryCharts'
import { getLyraTherapyAssessmentContentsData } from '../../../data/lyraTherapy/contentSelectors'
import { useGetFlexibleAssessmentsQuery } from '../data/flexibleAssessmentsApi'

type ClinicalAssessmentHistoryProps = {
  /** Assignments for the selected episode and recipient, of any group. */
  assignments?: Assignment[]
  /** Every assignment for the client; resolves self reflections by outcome assignment id. */
  selfReflectionAssignments?: Assignment[]
  canManageAssessments: boolean
  clientId?: string
  clientName?: string
  episodeId?: string
  recipientId?: string
  isLoading?: boolean
  isAssignmentsLoading: boolean
  hasAssignmentsError: boolean
  nextSessionDate?: Date
  isProviderSupervisor?: boolean
  outcomes?: Outcome[]
  programTaxonomy?: Partial<ProviderProgramTaxonomy> | null
  providerId?: string
  onAddAssessment?: () => void
}

const MESSAGES = defineMessages({
  assessmentDetailsModal: {
    defaultMessage: '{assessment}, submitted {date}',
    description: 'Accessibility label for a modal showing one submitted assessment',
  },
  selfReflectionTitle: {
    defaultMessage: 'Member Self Reflection',
    description: 'Title for a modal showing a member’s self-reflection response',
  },
  selfReflectionModal: {
    defaultMessage: 'Member Self Reflection, submitted {date}',
    description: 'Accessibility label for a modal showing a member’s self-reflection response',
  },
})

/** Gap between the sticky client profile header and the chart title once aligned. */
const CHART_SCROLL_GAP = 16

/** Where chart anchors should land: just below the sticky client profile header. */
const getChartTargetTop = () => {
  const header = document.querySelector<HTMLElement>('[data-testid="ClientProfileHeaderBar"]')
  return (header?.getBoundingClientRect().bottom ?? 0) + CHART_SCROLL_GAP
}

/**
 * Stops the page from jumping back to a chart after a modal closes.
 * "Go to chart" leaves focus on the chart heading (for screen readers). Row actions never take focus,
 * so if View answers is opened afterwards, closing that modal restores focus to the chart and the
 * browser scrolls back to it. Releasing the chart focus on the next mouse or touch press avoids that.
 */
const releaseFocusOnNextPointerDown = (element: HTMLElement) => {
  document.addEventListener(
    'pointerdown',
    () => {
      if (document.activeElement === element) element.blur()
    },
    { once: true },
  )
}

const SectionContainer = styled.View({
  width: '100%',
})

const SelfReflectionModalContent = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['16px'],
}))

const SelfReflectionModalHeader = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  gap: spacing['4px'],
  paddingTop: spacing['16px'],
}))

const SelfReflectionModalTitle = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingRight: spacing['40px'],
}))

const SelfReflectionModalText = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  paddingRight: spacing['16px'],
}))

const SelfReflectionModalBody: React.FC<ViewSelfReflectionRequest & { formattedDate: string; title: string }> = ({
  formattedDate,
  text,
  title,
}) => {
  const { colors } = useTheme()

  return (
    <SelfReflectionModalContent>
      <SelfReflectionModalHeader>
        <SelfReflectionModalTitle>
          <Title
            level='2'
            size={TitleSize.MEDIUM}
            testID={tID('ClinicalAssessmentHistory-selfReflectionTitle')}
            text={title}
          />
        </SelfReflectionModalTitle>
        <BodyText
          color={colors.textSecondary}
          size={BodyTextSize.DEFAULT}
          testID={tID('ClinicalAssessmentHistory-selfReflectionDate')}
          text={formattedDate}
        />
      </SelfReflectionModalHeader>
      <SelfReflectionModalText>
        <BodyText
          size={BodyTextSize.DEFAULT}
          testID={tID('ClinicalAssessmentHistory-selfReflectionText')}
          text={text}
          wrap
        />
      </SelfReflectionModalText>
    </SelfReflectionModalContent>
  )
}

export const ClinicalAssessmentHistory: React.FC<ClinicalAssessmentHistoryProps> = ({
  assignments,
  selfReflectionAssignments,
  canManageAssessments,
  clientId,
  clientName,
  episodeId,
  recipientId,
  isLoading,
  isAssignmentsLoading,
  hasAssignmentsError,
  nextSessionDate,
  isProviderSupervisor,
  outcomes,
  programTaxonomy,
  providerId,
  onAddAssessment,
}) => {
  const { formatDate, formatMessage } = useIntl()
  const [selectedDateKey, setSelectedDateKey] = useState<string | undefined>(undefined)
  const assessmentContents = useSelector(getLyraTherapyAssessmentContentsData)
  const [assessmentDetails, setAssessmentDetails] = useState<OutcomeHistoryChartResponse | null>(null)
  const [selfReflectionDetails, setSelfReflectionDetails] = useState<ViewSelfReflectionRequest | null>(null)
  const [isPendingAssessmentOpen, setIsPendingAssessmentOpen] = useState(false)

  // Provider Payments is web-only, so reaching for the DOM here is safe. The anchor is rendered
  // by OutcomeHistoryCharts below.
  const scrollToChart = useCallback((chartKey: string) => {
    const anchor = document.getElementById(getChartAnchorId(chartKey))
    if (anchor) {
      // scroll-margin keeps the chart clear of the sticky header. The header and anchor shrink
      // by the same amount during collapse, so the native scroll destination remains correct.
      anchor.style.scrollMarginTop = `${getChartTargetTop()}px`
      anchor.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      })
      // This is in-page navigation, so move semantic focus with the viewport. `preventScroll`
      // preserves the native smooth scroll already in progress while announcing the chart region.
      anchor.focus({ preventScroll: true })
      releaseFocusOnNextPointerDown(anchor)
    }
  }, [])

  const showPendingAssessment = useCallback(() => setIsPendingAssessmentOpen(true), [])

  const { currentData: flexibleAssessments, isFetching: isFetchingFlexibleAssessments } =
    useGetFlexibleAssessmentsQuery(episodeId ? { episodeId } : skipToken)
  const addedAssessmentsData = useAddedAssessmentsData({
    flexibleAssessments,
    assessmentContents,
    assignments,
    outcomes,
  })
  const isWaitingForFlexibleAssessments =
    Boolean(episodeId) && flexibleAssessments === undefined && isFetchingFlexibleAssessments
  const isAssessmentHistoryLoading = Boolean(isLoading) || isWaitingForFlexibleAssessments
  const selectedAddedAssessmentsData = useMemo(
    () => selectAddedAssessmentResults(addedAssessmentsData, selectedDateKey),
    [addedAssessmentsData, selectedDateKey],
  )
  const selfReflectionDate = parseOutcomeResponseDate(selfReflectionDetails?.dateSubmitted)
  const formattedSelfReflectionDate = selfReflectionDate
    ? formatDate(selfReflectionDate, { month: 'numeric', day: 'numeric', year: 'numeric' })
    : ''

  useEffect(() => setSelectedDateKey(undefined), [clientId, episodeId, recipientId])

  useEffect(() => {
    if (outcomes !== undefined && selectedDateKey !== undefined && !findOutcomeDateByKey(outcomes, selectedDateKey)) {
      setSelectedDateKey(undefined)
    }
  }, [outcomes, selectedDateKey])

  return (
    <SectionContainer testID={tID('ClinicalAssessmentHistory')}>
      <CurrentMeasuresTable
        assignments={assignments}
        selfReflectionAssignments={selfReflectionAssignments}
        clientName={clientName}
        isLoading={isAssessmentHistoryLoading}
        selectedDateKey={selectedDateKey}
        onSelectedDateKeyChange={setSelectedDateKey}
        isProviderSupervisor={isProviderSupervisor}
        outcomes={outcomes}
        programTaxonomy={programTaxonomy}
        providerId={providerId}
        onViewAnswers={setAssessmentDetails}
        onViewSelfReflection={setSelfReflectionDetails}
        onGoToChart={scrollToChart}
        onAddAssessment={canManageAssessments ? onAddAssessment : undefined}
        onViewPendingAssessment={showPendingAssessment}
        renderAddedAssessments={
          isAssessmentHistoryLoading
            ? undefined
            : ({ showSharedColumnHeaders }) => (
                <AddedAssessments
                  data={selectedAddedAssessmentsData}
                  canManageAssessments={canManageAssessments}
                  clientName={clientName}
                  isAssignmentsLoading={isAssignmentsLoading}
                  hasAssignmentsError={hasAssignmentsError}
                  nextSessionDate={nextSessionDate}
                  onViewAnswers={setAssessmentDetails}
                  onGoToChart={scrollToChart}
                  onViewPendingAssessment={showPendingAssessment}
                  showSharedColumnHeaders={showSharedColumnHeaders}
                />
              )
        }
      />
      <OutcomeHistoryCharts
        outcomes={outcomes ?? []}
        clientName={clientName ?? ''}
        answerModalAction={setAssessmentDetails}
        headingLevel={3}
        showHeading={false}
      />
      {/* Shared by the chart data points and the table's View answers row action. */}
      {assessmentDetails && (
        <BaseModal
          isOpen
          closeModal={() => setAssessmentDetails(null)}
          body={<OutcomeResponseDetails viewData={assessmentDetails} />}
          aria-label={formatMessage(MESSAGES.assessmentDetailsModal, {
            assessment: assessmentDetails.title,
            date: assessmentDetails.dateSubmitted,
          })}
          data-test-id='ClinicalAssessmentHistory-chartAssessmentDetailsModal'
        />
      )}
      {selfReflectionDetails && (
        <BaseModal
          isOpen
          closeModal={() => setSelfReflectionDetails(null)}
          body={
            <SelfReflectionModalBody
              {...selfReflectionDetails}
              formattedDate={formattedSelfReflectionDate}
              title={formatMessage(MESSAGES.selfReflectionTitle)}
            />
          }
          aria-label={formatMessage(MESSAGES.selfReflectionModal, { date: formattedSelfReflectionDate })}
          data-test-id='ClinicalAssessmentHistory-selfReflectionModal'
          size='medium'
        />
      )}
      <AssessmentPendingModal
        clientName={clientName}
        visible={isPendingAssessmentOpen}
        onRequestClose={() => setIsPendingAssessmentOpen(false)}
      />
    </SectionContainer>
  )
}
